import api from 'api';
import {get} from 'lodash';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {useMutation, useQuery} from 'react-query';
import {ListResponse} from 'types';
import {APIKey} from 'types/api-key';

const getApiKeys = async () => {
  const {data} = await api.get<ListResponse<APIKey>>('/companies/api-keys/');
  return data;
};

const deleteApiKey = async (id: number | undefined) => {
  if (!id) return;
  const {data} = await api.delete(`/companies/api-keys/${id}/`);
  return data;
};

const updateKey = async ({
  id,
  is_active,
  name,
}: {
  id: number;
  is_active?: boolean;
  name?: string;
}) => {
  const body = Object.fromEntries(
    Object.entries({is_active, name}).filter(
      ([_, value]) => value !== undefined
    )
  );
  return await api.patch(`/companies/api-keys/${id}/`, body);
};

const createApiKey = async ({
  name,
  is_active,
}: {
  name: string;
  is_active: boolean;
}) => {
  const body = {name, is_active};
  const {data} = await api.post('/companies/api-keys/', body);
  return data;
};

const API_KEY_LIST_SCHEMA = [
  {
    dataKey: 'name',
    header: 'Name',
    sortable: true,
    index: 0,
  },
  {
    dataKey: 'key',
    header: 'Key',
    sortable: true,
    index: 1,
  },
  {
    dataKey: 'created_at',
    header: 'Created At',
    sortable: true,
    index: 2,
  },
  {
    dataKey: 'is_active',
    header: 'Active',
    sortable: true,
    index: 3,
  },
  {
    dataKey: 'delete',
    header: <div style={{height: 40}}></div>,
    sortable: false,
    index: 4,
  },
];

export const useApiKeySettings = () => {
  const [isCreateKeyModalOpen, setIsCreateKeyModalOpen] = useState(false);
  const [isDeleteKeyModalOpen, setIsDeleteKeyModalOpen] = useState(false);
  const [deleteKeyId, setDeleteKeyId] = useState<number>();
  const {
    data: apiKeys,
    refetch,
    isLoading: isLoadingApiKeys,
  } = useQuery({
    queryKey: ['api-keys'],
    queryFn: getApiKeys,
  });
  const {mutateAsync: deleteApiKeyMutation, isLoading: isDeleting} =
    useMutation({
      mutationFn: deleteApiKey,
      onSuccess: () => {
        toast.success('API Key deleted successfully');
        refetch();
      },
      onError: (error) => {
        const message = get(
          error,
          'response.data.detail',
          'Failed to delete API Key'
        );
        toast.error(message);
      },
      onSettled: () => {
        setDeleteKeyId(undefined);
        setIsDeleteKeyModalOpen(false);
      },
    });

  const {mutateAsync: updateKeyMutation} = useMutation({
    mutationFn: updateKey,
    onSuccess: () => {
      toast.success('API Key updated successfully');
      refetch();
    },
    onError: (error) => {
      const message = get(
        error,
        'response.data.detail',
        'Failed to update API Key'
      );
      toast.error(message);
    },
  });
  const {mutateAsync: createApiKeyMutation, isLoading: isLoadingCreateKey} =
    useMutation({
      mutationFn: createApiKey,
      onSuccess: () => {
        toast.success('API Key created successfully');
        refetch();
      },
      onError: (error) => {
        const message = get(
          error,
          'response.data.detail',
          'Failed to create API Key'
        );
        toast.error(message);
      },
      onSettled: () => {
        setIsCreateKeyModalOpen(false);
      },
    });

  const handleDeleteKey = (id: number) => {
    if (!id) return;
    setDeleteKeyId(id);
    setIsDeleteKeyModalOpen(true);
  };

  return {
    data: apiKeys?.results || [],
    isLoadingApiKeys,
    API_KEY_LIST_SCHEMA,
    createApiKeyMutation,
    isCreateKeyModalOpen,
    setIsCreateKeyModalOpen,
    isLoadingCreateKey,
    isDeleteKeyModalOpen,
    setIsDeleteKeyModalOpen,
    deleteApiKeyMutation,
    isDeleting,
    updateKeyMutation,
    deleteKeyId,
    handleDeleteKey,
  };
};
